import { generateAvatarColor, getInitials } from '@/utils/avatar';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import PasswordIcon from '@mui/icons-material/Password';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';

import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { CountingButton } from '@/components/inputs/CountingButton/CountingButton';
import { EVALUATION } from '@/constant';
import { capture } from '@/handlers/error';
import { useSubscription } from '@/providers/subscription/SubscriptionProvider';
import { formatName } from '@/utils/name';
import { useApolloClient } from '@apollo/client';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, getGridStringOperators, GridActionsCellItem, GridColDef, GridFilterModel, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useGoogleLogin } from '@react-oauth/google';
import { useNotifications } from '@toolpad/core';
import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StudentImportGoogleClassroomDialog } from '../../experiments/StudentImportGoogleClassroomDialog/StudentImportGoogleClassroomDialog';
import { useSchool, useSession } from '../../providers/session/SessionProvider';
import StudentBulkCreateDialog from './components/StudentBulkCreateDialog';
import { StudentCreateDialog } from './components/StudentCreateDialog';
import { StudentDataGridToolbar } from './components/StudentDataGridToolbar';
import { StudentEditDialog } from './components/StudentEditDialog';
import { StudentPageHeader } from './components/StudentPageHeader';
import { StudentResetPasswordDialog } from './components/StudentResetPasswordDialog';
import StudentBadgeDialog from './containers/StudentBadgeDialog/StudentBadgeDialog';
import { StudentPrintBadgeDialog } from './containers/StudentPrintBadgeDialog/StudentPrintBadgeDialog';
import { parseFilterModel } from './filter';
import {
    CREATE_STUDENT,
    CREATE_STUDENTS,
    CreateStudentsVariables,
    CreateStudentVariables,
    GET_CLASSES,
    GET_STUDENTS,
    GetClassesResponse,
    GetStudentsResponse,
    UPDATE_STUDENT,
    UPDATE_USER,
    UpdateStudentVariables,
    UpdateUserVariables,
} from './gql';
import { ClassData, StudentData } from './types/student';

const defaultSortModel: { field: string; sort: 'asc' | 'desc' } = { field: 'name', sort: 'asc' };

interface StudensPageProps {
    initialState?: {
        pagination?: { paginationModel: GridPaginationModel };
    };
}

export const StudentsPage: React.FC<StudensPageProps> = ({ initialState }) => {
    // provider
    const { t } = useTranslation();
    const { session } = useSession();
    const { school } = useSchool();
    const { subscribed } = useSubscription();
    const notifications = useNotifications();
    const loginToGoogle = useGoogleLogin({
        scope: 'https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.rosters.readonly https://www.googleapis.com/auth/classroom.profile.emails',
        onSuccess: (response: { access_token: string }) => {
            setImportGoogleClassroomDialogToken(response.access_token);
        },
        onError: (error: unknown) => {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            capture(error);
        },
    });

    // params
    const className = new URLSearchParams(window.location.search).get('className') ?? undefined;

    // Initialize queryOption with filter if className exists
    const [queryOption, setQueryOption] = React.useState<
        | {
              pagination?: GridPaginationModel;
              sort?: GridSortModel;
              filter?: GridFilterModel;
          }
        | undefined
    >(() => {
        // If className is provided, include it in the filter
        if (className) {
            return {
                filter: {
                    items: [{ field: 'class', operator: 'equals', value: className }],
                },
                ...(initialState?.pagination?.paginationModel ? { pagination: initialState.pagination.paginationModel } : {}),
            };
        }

        // Otherwise just use initial pagination if available
        return initialState?.pagination?.paginationModel ? { pagination: initialState.pagination.paginationModel } : undefined;
    });

    // data grid
    const [data, setData] = React.useState<{ count: number; rows: StudentData[] } | undefined>(undefined);
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({
        type: 'include',
        ids: new Set(),
    });

    // ui
    const [loading, setLoading] = React.useState(true);
    const [createStudentData, setCreateStudentData] = React.useState<ClassData[] | null>(null);
    const [bulkCreateStudentData, setBulkCreateStudentData] = React.useState<ClassData[] | null>(null);
    const [editStudentData, setEditStudentData] = React.useState<StudentData | null>(null);
    const [resetPasswordStudentData, setResetPasswordStudentData] = React.useState<StudentData | null>(null);
    const [badgeStudentData, setBadgeStudentData] = React.useState<StudentData | null>(null);
    const [showStudentPrintBadgeDialog, setShowStudentPrintBadgeDialog] = React.useState(false);
    const [importGoogleClassroomDialogToken, setImportGoogleClassroomDialogToken] = React.useState<string | null>(null);
    const studentUserIds = React.useMemo(() => Array.from(rowSelectionModel.ids).map((id) => id.toString()), [rowSelectionModel]);
    const disabledStudentCreation = React.useMemo(() => data === undefined || (subscribed === false && data.count >= EVALUATION.MAX_STUDENTS), [subscribed, data]);

    // apollo client
    const client = useApolloClient();

    const refresh = React.useCallback(async () => {
        if (!queryOption?.pagination || !school?.id || !session?.token) {
            setData(undefined);
            return;
        }

        setLoading(true);
        const filters = queryOption.filter ? parseFilterModel(queryOption.filter) : [];
        try {
            const getClassesResponse = await client.query<GetClassesResponse>({ query: GET_CLASSES, variables: { schoolId: school?.id }, fetchPolicy: 'network-only' });
            const getStudentsResponse = await client.query<GetStudentsResponse>({
                query: GET_STUDENTS,
                fetchPolicy: 'network-only',
                variables: {
                    skip: queryOption.pagination.page * queryOption.pagination.pageSize,
                    take: queryOption.pagination.pageSize,
                    schoolId: school?.id,
                    orderBy: queryOption.sort?.[0] ? [{ [queryOption.sort[0].field]: queryOption.sort[0].sort }] : [{ [defaultSortModel.field]: defaultSortModel.sort }],
                    where: {
                        AND: [
                            { role: { equals: 'student' } },
                            {
                                studentProfile: {
                                    class: {
                                        school: {
                                            id: {
                                                equals: school?.id,
                                            },
                                        },
                                    },
                                },
                            },
                            ...filters,
                        ],
                    },
                },
            });

            setData({
                count: getStudentsResponse.data.usersCount,
                rows: getStudentsResponse.data.users.map((user) => ({
                    id: user.id,
                    name: formatName(user.studentProfile.familyName, user.studentProfile.givenName),
                    studentProfileId: user.studentProfile.id,
                    familyName: user.studentProfile.familyName,
                    givenName: user.studentProfile.givenName,
                    school: user.studentProfile.class.school.schoolName,
                    class: { id: user.studentProfile.class.id, name: user.studentProfile.class.className, schoolYear: user.studentProfile.class.schoolYear },
                    classes: getClassesResponse.data.classes,
                    studentNumber: user.studentProfile.studentNumber,
                    email: user.email,
                })),
            });
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
        }

        setLoading(false);
    }, [client, notifications, queryOption, school?.id, session?.token]);

    /**
     * callbacks
     */

    const handleCreateStudent = React.useCallback(async () => {
        await client
            .query<GetClassesResponse>({ query: GET_CLASSES, variables: { schoolId: school?.id } })
            .then((response) => {
                setCreateStudentData(response.data.classes);
            })
            .catch((error) => {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            });
    }, [client, notifications, school?.id]);

    const handleBulkCreateStudent = React.useCallback(async () => {
        await client
            .query<GetClassesResponse>({ query: GET_CLASSES, variables: { schoolId: school?.id } })
            .then((response) => {
                setBulkCreateStudentData(response.data.classes);
            })
            .catch((error) => {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            });
    }, [client, notifications, school?.id]);

    const handleImportGoogleClassroom = useCallback(async () => {
        loginToGoogle();
    }, [loginToGoogle]);

    const onPrint = React.useCallback(() => {
        if (rowSelectionModel.ids.size > 0) {
            setShowStudentPrintBadgeDialog(true);
        }
    }, [rowSelectionModel]);

    const onEdit = React.useCallback(async (user: StudentData) => {
        setEditStudentData(user);
    }, []);

    const onResetPassword = React.useCallback(async (user: StudentData) => {
        setResetPasswordStudentData(user);
    }, []);

    /**
     * end of callbacks
     */

    /**
     * effects
     */

    /**
     * refresh data when
     *   - pagination model changed
     *   - sort model changed
     *   - school changed
     *   - session changed (not likely)
     */
    React.useEffect(() => {
        refresh();
    }, [refresh]);

    /**
     * end of effects
     */

    const columns: GridColDef<StudentData>[] = React.useMemo(
        () => [
            {
                field: 'badge',
                headerName: t('student.label.badge'),
                headerAlign: 'center',
                align: 'center',
                disableColumnMenu: true,
                sortable: false,
                filterable: false,
                renderHeader: () => (
                    <Tooltip title={t('student.label.badge')}>
                        <BadgeIcon />
                    </Tooltip>
                ),
                renderCell: (params) => (
                    <Tooltip title={t('student.actions.studentBadge')}>
                        <IconButton
                            size='small'
                            onClick={(e) => {
                                e.stopPropagation();
                                setBadgeStudentData(params.row);
                            }}
                        >
                            <QrCodeIcon />
                        </IconButton>
                    </Tooltip>
                ),
            },
            {
                field: 'name',
                headerName: t('student.label.name'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                flex: 1.5,
                minWidth: 160,
                renderCell: (params) => {
                    const name = params.value || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <InitialalsAvatar
                                sx={{
                                    bgcolor: bg,
                                    color: text,
                                }}
                            >
                                {getInitials(name)}
                            </InitialalsAvatar>
                            <Typography variant='body2'>{name}</Typography>
                        </Stack>
                    );
                },
            },
            {
                field: 'school',
                headerName: t('student.label.school'),
                sortable: false,
                filterable: false,
                flex: 1,
                minWidth: 160,
                renderCell: (params) => {
                    const name = params.value || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <Chip
                                size='medium'
                                sx={{
                                    '& .MuiChip-avatar': {
                                        color: text,
                                    },
                                }}
                                avatar={
                                    <InitialalsAvatar
                                        sx={{
                                            bgcolor: bg,
                                        }}
                                    >
                                        {getInitials(name)}
                                    </InitialalsAvatar>
                                }
                                label={`${name}`}
                            />
                        </Stack>
                    );
                },
            },
            {
                field: 'class',
                headerName: t('student.label.class'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains' || operator.value === 'equals'),
                renderCell: (params) => {
                    const name = params.row.class.name || '';
                    const schoolYear = params.row.class.schoolYear || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <Chip
                                variant='filled'
                                size='medium'
                                sx={{
                                    '& .MuiChip-avatar': {
                                        color: text,
                                    },
                                }}
                                avatar={
                                    <InitialalsAvatar
                                        sx={{
                                            bgcolor: bg,
                                        }}
                                    >
                                        {getInitials(name)}
                                    </InitialalsAvatar>
                                }
                                label={`${name}${schoolYear ? ` (${schoolYear})` : ''}`}
                            />
                        </Stack>
                    );
                },
                flex: 1,
                minWidth: 160,
            },
            {
                field: 'studentNumber',
                headerName: t('student.label.number'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                flex: 0.5,
                minWidth: 160,
            },
            {
                field: 'email',
                headerName: t('student.label.email'),
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                flex: 1.5,
                minWidth: 200,
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: t('common.actions'),
                cellClassName: 'actions',
                getActions: (params) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('student.actions.editStudent')}>
                                <EditIcon />
                            </Tooltip>
                        }
                        label='edit'
                        aria-label='edit'
                        className='textPrimary'
                        onClick={() => onEdit(params.row)}
                        color='inherit'
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('student.actions.resetPassword')}>
                                <PasswordIcon />
                            </Tooltip>
                        }
                        label='reset-password'
                        aria-label='reset-password'
                        className='textPrimary'
                        onClick={() => onResetPassword(params.row)}
                        color='inherit'
                    />,
                ],
            },
        ],
        [onEdit, onResetPassword, t],
    );

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: 16 }}>
                <Stack sx={{ flex: 1, overflow: 'hidden' }} spacing={2}>
                    <StudentPageHeader
                        onCreate={handleCreateStudent}
                        onBulkCreate={handleBulkCreateStudent}
                        onImportGoogleClassroom={handleImportGoogleClassroom}
                        disabled={disabledStudentCreation}
                        tooltip={disabledStudentCreation ? t('trial.tooltip.student', { count: EVALUATION.MAX_STUDENTS }) : undefined}
                    />
                    <DataGrid
                        columns={columns}
                        autoPageSize
                        filterMode='server'
                        sortingMode='server'
                        paginationMode='server'
                        filterModel={queryOption?.filter}
                        initialState={{
                            sorting: { sortModel: [defaultSortModel] },
                        }}
                        onPaginationModelChange={(model) => {
                            setQueryOption((prev) => (prev ? { ...prev, pagination: model } : { pagination: model }));
                        }}
                        onSortModelChange={(model) => {
                            setQueryOption((prev) => (prev ? { ...prev, sort: model } : { sort: model }));
                        }}
                        onFilterModelChange={(model) => {
                            setQueryOption((prev) => (prev ? { ...prev, filter: model } : { filter: model }));
                        }}
                        rows={data?.rows}
                        rowCount={data?.count || 0}
                        loading={loading}
                        slots={{
                            toolbar: StudentDataGridToolbar,
                        }}
                        slotProps={{
                            loadingOverlay: {
                                variant: 'linear-progress',
                                noRowsVariant: 'linear-progress',
                            },
                            toolbar: {
                                additionalTool: {
                                    right: (
                                        <CountingButton variant='text' count={rowSelectionModel.ids.size} endIcon={<PrintIcon />} onClick={onPrint}>
                                            {t('student.button.printBadges')}
                                        </CountingButton>
                                    ),
                                },
                            },
                        }}
                        showToolbar
                        onRowSelectionModelChange={(model) => {
                            setRowSelectionModel(model);
                        }}
                        checkboxSelection
                        keepNonExistentRowsSelected
                    />
                </Stack>
            </div>
            {editStudentData && (
                <StudentEditDialog
                    open={!!editStudentData}
                    payload={{
                        data: { user: editStudentData },
                    }}
                    onClose={async (data) => {
                        if (data) {
                            try {
                                await client.mutate<unknown, UpdateStudentVariables>({
                                    mutation: UPDATE_STUDENT,
                                    variables: {
                                        studentProfileUpdateInput: {
                                            familyName: data.familyName,
                                            givenName: data.givenName,
                                            studentNumber: data.studentNumber,
                                            class: {
                                                connect: {
                                                    id: data.class.id,
                                                },
                                            },
                                        },
                                        studentProfileId: data.studentProfileId,
                                    },
                                });
                                /**
                                 * Update user record, e.g. email, is not allowed at the moment
                                 */
                                // await client.mutate<unknown, UpdateUserVariables>({
                                //     mutation: UPDATE_USER,
                                //     variables: {
                                //         userUpdateInput: {
                                //             email: data.email,
                                //             name: data.name,
                                //         },
                                //         id: data.id,
                                //     },
                                // });
                                setEditStudentData(null);
                                refresh();
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        } else {
                            setEditStudentData(null);
                        }
                    }}
                />
            )}
            {resetPasswordStudentData && (
                <StudentResetPasswordDialog
                    open={!!resetPasswordStudentData}
                    onClose={async (password) => {
                        if (password) {
                            try {
                                await client.mutate<unknown, UpdateUserVariables>({
                                    mutation: UPDATE_USER,
                                    variables: {
                                        userUpdateInput: {
                                            password,
                                        },
                                        id: resetPasswordStudentData?.id,
                                    },
                                });
                                setResetPasswordStudentData(null);
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        } else {
                            setResetPasswordStudentData(null);
                        }
                    }}
                    payload={undefined}
                />
            )}
            {badgeStudentData && <StudentBadgeDialog open={!!badgeStudentData} onClose={() => setBadgeStudentData(null)} data={badgeStudentData} />}
            {/* intentional to mount/unmount using `showCreateStudentDialog` in order to clear the dialog state within this page */}
            {createStudentData && (
                <StudentCreateDialog
                    open={!!createStudentData}
                    onClose={async (data) => {
                        if (data) {
                            try {
                                const result = await client.mutate<unknown, CreateStudentVariables>({
                                    mutation: CREATE_STUDENT,
                                    variables: {
                                        userCreateInput: {
                                            email: data.email,
                                            password: data.password,
                                            role: 'student',
                                            name: data.name,
                                            studentProfile: {
                                                create: {
                                                    studentNumber: data.studentNumber,
                                                    familyName: data.familyName,
                                                    givenName: data.givenName,
                                                    class: data.class
                                                        ? {
                                                              connect: {
                                                                  id: data.class.id,
                                                              },
                                                          }
                                                        : undefined,
                                                },
                                            },
                                        },
                                    },
                                    errorPolicy: 'all',
                                });

                                if (result.errors && result.errors.length > 0) {
                                    const error = result.errors[0];

                                    if (error && error.extensions?.code === 'KS_ACCESS_DENIED') {
                                        notifications.show(t('trial.error.student'), { severity: 'error', autoHideDuration: 5000 });
                                    } else {
                                        notifications.show(error?.message, { severity: 'error', autoHideDuration: 5000 });
                                    }

                                    return;
                                }

                                setCreateStudentData(null);
                                refresh();
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        } else {
                            setCreateStudentData(null);
                        }
                    }}
                    payload={{
                        data: {
                            classes: createStudentData,
                        },
                    }}
                />
            )}
            {/* intentional to mount/unmount using `showCreateStudentDialog` in order to clear the dialog state within this page */}
            {bulkCreateStudentData && (
                <StudentBulkCreateDialog
                    open={!!bulkCreateStudentData}
                    payload={{
                        classes: bulkCreateStudentData,
                    }}
                    onClose={async (data) => {
                        if (data) {
                            try {
                                const result = await client.mutate<unknown, CreateStudentsVariables>({
                                    mutation: CREATE_STUDENTS,
                                    variables: {
                                        userCreateInputs: data.map((student) => ({
                                            email: student.email,
                                            password: student.password,
                                            role: 'student',
                                            name: student.name,
                                            studentProfile: {
                                                create: {
                                                    studentNumber: student.studentNumber,
                                                    familyName: student.familyName,
                                                    givenName: student.givenName,
                                                    class: student.class
                                                        ? {
                                                              connect: {
                                                                  id: student.class.id,
                                                              },
                                                          }
                                                        : undefined,
                                                },
                                            },
                                        })),
                                    },
                                    errorPolicy: 'all',
                                });

                                if (result.errors && result.errors.length > 0) {
                                    const error = result.errors[0];

                                    if (error && error.extensions?.code === 'KS_ACCESS_DENIED') {
                                        notifications.show(t('trial.error.student'), { severity: 'error', autoHideDuration: 5000 });
                                    } else {
                                        notifications.show(error?.message, { severity: 'error', autoHideDuration: 5000 });
                                    }

                                    return;
                                }

                                setBulkCreateStudentData(null);
                                refresh();
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                            }
                        } else {
                            setBulkCreateStudentData(null);
                        }
                    }}
                />
            )}

            <StudentPrintBadgeDialog open={showStudentPrintBadgeDialog} onClose={() => setShowStudentPrintBadgeDialog(false)} userIds={studentUserIds} />

            {importGoogleClassroomDialogToken && (
                <StudentImportGoogleClassroomDialog
                    open={!!importGoogleClassroomDialogToken}
                    onClose={() => {
                        setImportGoogleClassroomDialogToken(null);
                    }}
                    accessToken={importGoogleClassroomDialogToken}
                />
            )}
        </>
    );
};
